import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { KeyOutlined, LogoutOutlined, SettingFilled, UploadOutlined } from '@ant-design/icons/lib';
import { useAuth0 } from '@auth0/auth0-react';
import { Avatar, Dropdown, MenuProps } from 'antd';
import classNames from 'classnames';
import styled, { createGlobalStyle } from 'styled-components';

import { wasAuthenticatedViaAuth0 } from 'auth/auth0Config';
import Auth0PasswordResetModal from 'modals/Auth0PasswordResetModal';
import ChangeAvatarModal from 'modals/ChangeAvatar';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleLogout } from 'store/slices/authSlice';
import { handleReset } from 'store/slices/globalSlice';

import axios from '../../../config/axiosPrivate';

const HeaderUserDropdownStyles = createGlobalStyle`
  .header-user-actions-dropdown .ant-dropdown-menu,
  .header-user-actions-submenu .ant-dropdown-menu {
    padding: 0;
    border-radius: 0;
    border: 1px solid var(--line-gray);
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  }

  .header-user-actions-dropdown .ant-dropdown-menu-item,
  .header-user-actions-dropdown .ant-dropdown-menu-submenu-title,
  .header-user-actions-submenu .ant-dropdown-menu-item {
    min-width: 200px;
    padding: 6px 10px;
    border-radius: 0;
    font-size: 14px;
  }
`;

const UserContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 15px;
`;

const UserNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 0;
  text-align: right;
`;

const UserName = styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.1;
`;

const UserEmail = styled.span`
  font-size: 11px;
  line-height: 1;
  color: var(--text-secondary);
`;

const StyledAvatar = styled(Avatar)`
  background: var(--light-gray);
  color: var(--text-secondary);
  border: 1px solid var(--gray-dark);

  &.has-image {
    border: none;
  }

  .ant-avatar-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-avatar-icon svg {
    width: 16px;
    height: 16px;
  }
`;

const FallbackUserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5.5 18.5C6.6 15.9 8.9 14.5 12 14.5C15.1 14.5 17.4 15.9 18.5 18.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

type Props = {
  showUserFullName?: boolean;
};

const HeaderUserActions = ({ showUserFullName = true }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentUser, accessToken } = useAppSelector((state) => state.auth);
  const { logout: auth0Logout } = useAuth0();

  const [openAvatarModal, setOpenAvatarModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const isAuth0User = wasAuthenticatedViaAuth0(accessToken);

  const onLogoutUser = async () => {
    // Check auth method before clearing tokens
    await axios.get(`api/jwt/auth/logout/`).then(() => {
      dispatch(handleLogout());
      dispatch(handleReset());

      // Only logout from Auth0 if user authenticated via Auth0
      if (isAuth0User) {
        auth0Logout({ logoutParams: { returnTo: window.location.origin + '/sign-in' } });
      } else {
        navigate('/sign-in');
      }
    });
  };

  // TODO: Remove settings completely from the New Version header after SWOT updates are enabled through New Version widgets
  const showSettings =
    currentUser?.isSuperuser || currentUser?.all_permissions?.indexOf('PAScreens.can_view_swot_analysis') != -1;

  const settings = {
    label: 'Settings',
    key: '1',
    icon: <SettingFilled />,
    popupClassName: 'header-user-actions-submenu',
    style: {
      minWidth: 200,
    },
    onClick: () => {
      // Do nothing
    },
    children: [
      currentUser?.all_permissions?.indexOf('PAScreens.can_view_swot_analysis') != -1 || currentUser?.isSuperuser
        ? {
            label: 'SWOT Survey',
            key: 'swot_survey',
            style: {
              minWidth: 200,
            },
            onClick: () => navigate(`swot/swot-survey`),
          }
        : null,
      currentUser?.all_permissions?.indexOf('PAScreens.can_view_swot_analysis') != -1 || currentUser?.isSuperuser
        ? {
            label: 'SWOT Survey List',
            key: 'swot_survey_list',
            style: {
              minWidth: 200,
            },
            onClick: () => navigate(`swot/swot-survey-list`),
          }
        : null,
    ],
  };

  const items: MenuProps['items'] = [
    showSettings ? settings : null,
    {
      label: 'Change Avatar',
      key: 'Change Avatar',
      icon: <UploadOutlined />,
      style: {
        minWidth: 200,
      },
      onClick: () => setOpenAvatarModal(true),
    },
    isAuth0User
      ? {
          label: 'Change Password',
          key: 'Change Password',
          icon: <KeyOutlined />,
          style: {
            minWidth: 200,
          },
          onClick: () => setOpenPasswordModal(true),
        }
      : null,
    {
      label: 'Logout',
      key: '0',
      icon: <LogoutOutlined />,
      style: {
        minWidth: 200,
      },
      onClick: onLogoutUser,
    },
  ];

  return (
    <div>
      <HeaderUserDropdownStyles />
      <Auth0PasswordResetModal
        open={isAuth0User && openPasswordModal}
        handleClose={() => setOpenPasswordModal(false)}
        title="Change Password"
      />
      <ChangeAvatarModal open={openAvatarModal} handleClose={() => setOpenAvatarModal(false)} title="Change Avatar" />
      <Dropdown
        menu={{ items }}
        placement="bottomRight"
        arrow
        trigger={['click']}
        overlayClassName="header-user-actions-dropdown"
      >
        <UserContainer>
          {showUserFullName && (
            <UserNameContainer>
              <UserName>{currentUser?.fname || currentUser?.FullName || currentUser?.email}</UserName>
              {currentUser?.email && <UserEmail>{currentUser.email}</UserEmail>}
            </UserNameContainer>
          )}
          {currentUser?.avatar ? (
            <StyledAvatar
              className={classNames({ 'has-image': currentUser?.avatar })}
              size={30}
              src={currentUser?.avatar}
            />
          ) : (
            <StyledAvatar
              className={classNames({ 'has-image': currentUser?.avatar })}
              size={30}
              icon={<FallbackUserIcon />}
            />
          )}
        </UserContainer>
      </Dropdown>
    </div>
  );
};

export default HeaderUserActions;
